import { Logger } from '@aircall/logger';
import type { UserRepository } from '@infra/repositories/user.repository';
import { CallInfoGenerator } from '@main/generators/call-info.generator';
import { isBlindTransferEnable } from '@main/utils/isBlindTransfer';
import { ScvObjectManager } from '@main/sf-connector/scv-object-manager';
import type {
  AgentConfig,
  HangupResult,
  PhoneCall,
  Contact,
  SupervisedCallInfo,
} from '@salesforce/scv-connector-base';
import {
  ActiveCallsResult,
  AgentConfigResult,
  CallInfo,
  CallResult,
  CapabilitiesResult,
  Constants,
  GenericResult,
  HoldToggleResult,
  MuteToggleResult,
  ParticipantResult,
  PhoneCallAttributes,
  PhoneContactsResult,
  publishEvent,
  RecordingToggleResult,
  SignedRecordingUrlResult,
  SuperviseCallResult,
  SupervisorHangupResult,
  TelephonyConnector,
} from '@salesforce/scv-connector-base';
import { CallTypes } from '../../entities/call';
import { CallStatuses } from '../../entities/call';
import { ParticipantType } from '../../entities/participants';
import type { CallRepository } from '../../infra/repositories/call.repository';
import { ControllerFactory } from '../factories/controller.factory';
import { RepositoryFactory } from '../factories/repository.factory';
import { CallResultGenerator } from '../generators/call-result.generator';
import { HangupResultGenerator } from '../generators/hangup-result.generator';
import { PhoneCallGenerator } from '../generators/phone-call.generator';
import sleep from '../utils/sleep';
import uuid from '../utils/uuid';
import type { ScvPhoneCall } from './scv-connector';
export class PhoneConnector extends TelephonyConnector {
  [x: string]: any;
  private callRepository: CallRepository;
  private userRepository: UserRepository;
  private logger: Logger;

  constructor() {
    super();
    this.callRepository = RepositoryFactory.buildMemCallRepository();
    this.userRepository = RepositoryFactory.buildMemUserRepository();
    this.logger = new Logger();
  }

  override async acceptCall({
    recordId,
    callId,
  }: ScvPhoneCall): Promise<CallResult> {
    const controller = ControllerFactory.buildAcceptIncomingCallController();

    await controller.handle({
      recordId,
    });

    const call = await this.callRepository.fetchCallById(callId);

    if (call) {
      const callResult = CallResultGenerator.generate(
        call.id,
        this.mapCallType(call.type),
        call.from,
      );

      const callDetails = await this.callRepository.fetchCallById(call.id);
      const userDetails = this.userRepository.getUserInfo();

      if (callDetails && userDetails?.numbers) {
        const matchingNumber = userDetails.numbers.find(
          (number) => number.number === callDetails.to,
        );

        console.log(matchingNumber);

        if (matchingNumber) {
          const {
            inbound_recording_activated,
            allow_inbound_recording_actions,
          } = matchingNumber;

          if (call.type === CallTypes.INBOUND) {
            callResult.call.callInfo =
              CallInfoGenerator.generateWithRecordingCapabilities(
                call.id,
                !inbound_recording_activated,
                allow_inbound_recording_actions,
              );
          }
        }
      }

      callResult.call.state = Constants.CALL_STATE.CONNECTED;

      this.logger.info('Incoming call connected', {
        action: 'AcceptIncomingCall',
        callId,
        callResult,
      });

      return callResult;
    }

    this.logger.warn('No call found', {
      action: 'AcceptIncomingCall',
      callId,
    });

    return new CallResult({});
  }

  override async endCall(
    phoneCall: PhoneCall,
    agentStatus: Constants.AGENT_STATUS,
  ): Promise<HangupResult> {
    const is_AcwEnabled = await ScvObjectManager.checkAfterConversationWork();
    if (is_AcwEnabled) {
      const acwStartedController =
        ControllerFactory.buildWrapUpTimeStartedController();
      await acwStartedController.handle();
    }

    let controller;
    let currentcall;
    if (phoneCall && phoneCall.callId) {
      const currentCall = await this.callRepository.getCurrentCall();
      if (currentCall) {
        const isOnHold = currentCall.isOnHold;
        if (isOnHold) {
          const holdController = ControllerFactory.buildHoldToggleController();
          await holdController.handle({
            onHold: false,
            participantType: ParticipantType.EXTERNAL,
          });
        }
      }
      controller = ControllerFactory.buildHangupTransferCallController();
      currentcall = await this.callRepository.fetchTransferCall();
    } else {
      controller = ControllerFactory.buildHangupCallController();
      currentcall = await this.callRepository.getCurrentCall();
    }

    if (!currentcall) {
      this.logger.warn('No call found to end', {
        action: 'HangupCall',
        phoneCall,
        agentStatus,
      });
    }

    const { id, type, from } = currentcall;

    this.logger.info('End call', {
      action: 'HangupCall',
      phoneCall,
      agentStatus,
      id,
    });

    const hangUpResult = HangupResultGenerator.generate(
      id,
      this.mapCallType(type),
      from,
    );
    await controller.handle();
    await sleep(100);

    return hangUpResult;
  }

  override async declineCall(phoneCall: PhoneCall): Promise<CallResult> {
    if (!phoneCall) {
      return new CallResult({});
    }

    const { callId } = phoneCall;
    const controller = ControllerFactory.buildRejectIncomingCallController();

    this.logger.info('Decline incoming call', {
      action: 'RejectIncomingCall',
      phoneCall,
      id: callId,
    });

    const call = await controller.handle({ callId });

    if (call) {
      return CallResultGenerator.generate(
        call.id,
        this.mapCallType(call.type),
        call.from,
      );
    }

    this.logger.info('No incoming call found', {
      action: 'RejectIncomingCall',
      phoneCall,
      id: callId,
    });

    return new CallResult({});
  }

  override async getActiveCalls(): Promise<ActiveCallsResult> {
    const call = await this.callRepository.getCurrentCall();

    if (call) {
      const { id, type, from, to, isOnHold = false } = call;
      const phoneCall = PhoneCallGenerator.generate(
        id,
        this.mapCallType(type),
        type === CallTypes.INBOUND ? from : to,
      );
      phoneCall.callInfo = new CallInfo({
        isOnHold,
        isRecordingPaused: false,
        isMuted: false,
      });

      return new ActiveCallsResult({ activeCalls: [phoneCall] });
    }
    return new ActiveCallsResult({ activeCalls: [] });
  }

  private mapCallType = (type: CallTypes): Constants.CALL_TYPE => {
    return type === CallTypes.INBOUND
      ? Constants.CALL_TYPE.INBOUND
      : type === CallTypes.TRANSFER
      ? Constants.CALL_TYPE.TRANSFER
      : type === CallTypes.ADD_PARTICIPANT
      ? Constants.CALL_TYPE.ADD_PARTICIPANT
      : Constants.CALL_TYPE.OUTBOUND;
  };

  override async dial({ phoneNumber }: Contact): Promise<CallResult> {
    try {
      const callId = uuid();
      const startTime = new Date().toISOString();

      const callUuidController =
        ControllerFactory.buildRecordCallUuidController();
      console.log('sending action resource', {
        phoneNumber,
        callId,
        timestamp: startTime,
      });

      const recordUuidStartTime = new Date().toISOString();
      try {
        await callUuidController.handle({ phoneNumber, callUuid: callId });
        console.log('action resource recorded', {
          callId,
          timestamp: new Date().toISOString(),
          recordUuidStartTime,
          duration: Date.now() - new Date(recordUuidStartTime).getTime(),
        });
      } catch (error) {
        console.log('error while recording call uuid', {
          error,
          timestamp: new Date().toISOString(),
        });
      }

      const controller = ControllerFactory.buildDialNumberController();

      this.logger.info('Dialing number', {
        action: 'DialNumber',
        phoneNumber,
        id: callId,
        timestamp: new Date().toISOString(),
      });

      const dialStartTime = new Date().toISOString();
      await controller.handle({
        callId,
        phoneNumber,
      });

      console.log('dial controller completed', {
        callId,
        timestamp: new Date().toISOString(),
        dialStartTime,
        duration: Date.now() - new Date(dialStartTime).getTime(),
      });

      const fetchCallStartTime = new Date().toISOString();
      const call = await this.callRepository.fetchCallById(callId);

      console.log('call fetched from repository', {
        callId,
        callFound: !!call,
        timestamp: new Date().toISOString(),
        fetchCallStartTime,
        duration: Date.now() - new Date(fetchCallStartTime).getTime(),
      });

      if (call) {
        const callResult = CallResultGenerator.generate(
          call.id,
          this.mapCallType(call.type),
          call.to,
        );

        console.log('dial operation completed successfully', {
          callId,
          timestamp: new Date().toISOString(),
          totalDuration: Date.now() - new Date(startTime).getTime(),
        });

        return callResult;
      }

      console.log('dial operation completed - no call found', {
        callId,
        timestamp: new Date().toISOString(),
        totalDuration: Date.now() - new Date(startTime).getTime(),
      });

      return new CallResult({});
    } catch (err) {
      console.log('dial operation failed', {
        error: err,
        timestamp: new Date().toISOString(),
      });
      return new CallResult({});
    }
  }

  override async mute(): Promise<MuteToggleResult> {
    const controller = ControllerFactory.buildMuteToggleController();
    const putOnMute = true;
    await controller.handle({ onMute: putOnMute });
    return new MuteToggleResult({ isMuted: putOnMute });
  }

  override async unmute(): Promise<MuteToggleResult> {
    const controller = ControllerFactory.buildMuteToggleController();
    const putOnMute = false;
    await controller.handle({ onMute: putOnMute });
    return new MuteToggleResult({ isMuted: putOnMute });
  }

  override async hold(phoneCall: PhoneCall): Promise<HoldToggleResult> {
    const controller = ControllerFactory.buildHoldToggleController();
    const toggleResult = new HoldToggleResult({
      isCustomerOnHold: false,
      isThirdPartyOnHold: false,
    });
    if (
      phoneCall.callAttributes.participantType ===
      Constants.PARTICIPANT_TYPE.THIRD_PARTY
    ) {
      controller.handle({
        onHold: true,
        participantType: ParticipantType.THIRD_PARTY,
      });
      const currentCall = await this.callRepository.getCurrentCall();
      if (currentCall) {
        toggleResult.isCustomerOnHold = currentCall.isOnHold ?? false;
      }
      toggleResult.isThirdPartyOnHold = true;
    } else if (
      phoneCall.callAttributes.participantType ===
      Constants.PARTICIPANT_TYPE.INITIAL_CALLER
    ) {
      controller.handle({
        onHold: true,
        participantType: ParticipantType.EXTERNAL,
      });
      const transferCall = await this.callRepository.fetchTransferCall();
      if (transferCall) {
        toggleResult.isThirdPartyOnHold = transferCall.isOnHold ?? false;
      }
      toggleResult.isCustomerOnHold = true;
    }
    return toggleResult;
  }

  override async resume(phoneCall: PhoneCall): Promise<HoldToggleResult> {
    const controller = ControllerFactory.buildHoldToggleController();
    const toggleResult = new HoldToggleResult({
      isCustomerOnHold: false,
      isThirdPartyOnHold: false,
    });
    if (
      phoneCall.callAttributes.participantType ===
      Constants.PARTICIPANT_TYPE.THIRD_PARTY
    ) {
      controller.handle({
        onHold: false,
        participantType: ParticipantType.THIRD_PARTY,
      });
      const currentCall = await this.callRepository.getCurrentCall();
      if (currentCall) {
        toggleResult.isCustomerOnHold = currentCall.isOnHold ?? false;
      }
      toggleResult.isThirdPartyOnHold = false;
    } else if (
      phoneCall.callAttributes.participantType ===
      Constants.PARTICIPANT_TYPE.INITIAL_CALLER
    ) {
      controller.handle({
        onHold: false,
        participantType: ParticipantType.EXTERNAL,
      });
      const transferCall = await this.callRepository.fetchTransferCall();
      if (transferCall) {
        toggleResult.isThirdPartyOnHold = transferCall.isOnHold ?? false;
      }
      toggleResult.isCustomerOnHold = false;
    }
    return toggleResult;
  }

  override sendDigits(digits: string) {
    console.log('[Aircall Connector] sendDigits method', { digits });
  }

  override async getPhoneContacts(): Promise<PhoneContactsResult> {
    try {
      const controller = ControllerFactory.buildShowAgentListController();
      const contacts = (await controller.handle()) as Contact[];
      return Promise.resolve(
        new PhoneContactsResult({
          contacts,
          contactTypes: [Constants.CONTACT_TYPE.AGENT],
        }),
      );
    } catch (err) {
      return Promise.resolve(new PhoneContactsResult({}));
    }
  }

  override async swap(
    call1: PhoneCall,
    call2: PhoneCall,
  ): Promise<HoldToggleResult> {
    this.logger.info('Swap call', {
      action: 'SwapCall',
      call1,
      call2,
    });
    let isThirdPartyOnHold = false;
    let isCustomerOnHold = false;
    const controller = ControllerFactory.buildSwapCallController();
    const Transfercall = await this.callRepository.fetchTransferCall();
    if (Transfercall) {
      isThirdPartyOnHold = !Transfercall.isOnHold;
    }
    const currentCall = await this.callRepository.getCurrentCall();
    if (currentCall) {
      isCustomerOnHold = !currentCall.isOnHold;
    }
    setTimeout(() => {
      this.unmute();
    }, 6000);
    setTimeout(() => {
      const muteagent = ControllerFactory.buildMuteAgentController();
      muteagent.handle({ onMute: false });
    }, 1000);
    controller.handle();
    return Promise.resolve(
      new HoldToggleResult({
        isThirdPartyOnHold: isThirdPartyOnHold,
        isCustomerOnHold: isCustomerOnHold,
      }),
    );
  }

  override conference(calls: PhoneCall[]): Promise<HoldToggleResult> {
    console.log('[Aircall Connector] conference method', { calls });
    const controller = ControllerFactory.buildWarmTransferMergeController();
    this.logger.info('START MERGE WARM TRANSFER', {
      action: 'STARTMERGEWARMTRANSFER',
    });
    controller.handle();
    return Promise.resolve(
      new HoldToggleResult({
        isThirdPartyOnHold: true,
        isCustomerOnHold: true,
      }),
    );
  }

  override async addParticipant(
    contact: Contact,
    call: PhoneCall,
    isBlindTransfer: boolean,
  ): Promise<ParticipantResult> {
    try {
      const phoneNumber = contact.phoneNumber;
      const callId = call.callId;

      if (isBlindTransfer) {
        const blindTransferController =
          ControllerFactory.buildBlindTransferNumberController();
        await blindTransferController.handle({
          callId,
          phoneNumber: contact.phoneNumber || `client:${contact.id}`,
          name: contact.name,
        });
        // Wait for a short period to ensure the transfer is initiated
        setTimeout(async () => {
          this.unmute();
        }, 600);

        return Promise.resolve(
          new ParticipantResult({
            initialCallHasEnded: true,
            callInfo: new CallInfo({
              isOnHold: false,
              isRecordingPaused: false,
              isMuted: false,
              holdEnabled: false,
              swapEnabled: false,
              removeParticipantVariant:
                Constants.REMOVE_PARTICIPANT_VARIANT.ALWAYS,
            }),
            phoneNumber: contact.name,
            callId: callId,
          }),
        );
      } else {
        // Existing warm transfer logic
        const controller = ControllerFactory.buildTransferNumberController();
        this.logger.info('Dialing number', {
          action: 'DialNumber',
          phoneNumber,
          id: callId,
        });
        if (contact.phoneNumber) {
          await controller.handle({
            callId,
            phoneNumber,
            name: contact.name,
          });
        } else if (contact.id) {
          await controller.handle({
            callId,
            phoneNumber: 'client:' + contact.id,
            name: contact.name,
          });
        }
        setTimeout(async () => {
          this.unmute();
        }, 6000);
        return Promise.resolve(
          new ParticipantResult({
            initialCallHasEnded: false,
            callInfo: new CallInfo({
              isOnHold: false,
              isRecordingPaused: false,
              isMuted: false,
              holdEnabled: false,
              swapEnabled: true,
              removeParticipantVariant:
                Constants.REMOVE_PARTICIPANT_VARIANT.ALWAYS,
            }),
            phoneNumber: contact.name,
            callId: callId,
          }),
        );
      }
    } catch (err) {
      console.log('Error in addParticipant', { err });
      return Promise.resolve(
        new ParticipantResult({
          initialCallHasEnded: false,
          callInfo: new CallInfo({
            isOnHold: false,
            isRecordingPaused: false,
            isMuted: false,
            holdEnabled: false,
          }),
          phoneNumber: '',
          callId: '',
        }),
      );
    }
  }

  override async pauseRecording(): Promise<RecordingToggleResult> {
    console.log('[Aircall Connector] pauseRecording method');
    const controller = ControllerFactory.buildRecordingToggleController();
    await controller.handle({ onRecordStart: false });
    return new RecordingToggleResult({ isRecordingPaused: true });
  }

  override async resumeRecording(): Promise<RecordingToggleResult> {
    console.log('[Aircall Connector] resumeRecording method');
    const controller = ControllerFactory.buildRecordingToggleController();
    await controller.handle({ onRecordStart: true });
    return new RecordingToggleResult({ isRecordingPaused: false });
  }

  override getAgentConfig(): Promise<AgentConfigResult> {
    console.log('[Aircall Connector] getAgentConfig method');
    return Promise.resolve(new AgentConfigResult(<any>{}));
  }

  override setAgentConfig(config: AgentConfig): Promise<GenericResult> {
    console.log('[Aircall Connector] setAgentConfig method', { config });
    return Promise.resolve(new GenericResult({ success: true }));
  }

  override getCapabilities(): Promise<CapabilitiesResult> {
    this.logger.info('Get capabilities', {
      action: 'GetCapabilities',
    });
    return Promise.resolve(
      new CapabilitiesResult({
        hasMute: true,
        hasRecord: true,
        hasMerge: true,
        hasSwap: true,
        hasSignedRecordingUrl: true,
        debugEnabled: false,
        hasBlindTransfer: isBlindTransferEnable(),
        hasSupervisorListenIn: true,
        hasSupervisorBargeIn: true,
      }),
    );
  }

  override async getSignedRecordingUrl(
    recordingUrl: string,
    vendorCallKey: string,
    callId: string,
  ): Promise<SignedRecordingUrlResult> {
    console.log('[Aircall Connector] getSignedRecordingUrl method', {
      recordingUrl,
      vendorCallKey,
      callId,
    });
    const controller = ControllerFactory.buildGetSignedRecordingController();
    const { url } = await controller.handle({
      recordingUrl,
    });
    return new SignedRecordingUrlResult({
      success: true,
      url: url,
      callId: callId,
    });
  }

  override async superviseCall(
    supervisedCallInfo: SupervisedCallInfo,
  ): Promise<SuperviseCallResult> {
    console.log('[Aircall Connector] superviseCall method', {
      supervisedCallInfo,
    });
    const callType =
      supervisedCallInfo.callType == Constants.CALL_TYPE.INBOUND
        ? Constants.CALL_TYPE.INBOUND
        : Constants.CALL_TYPE.OUTBOUND;
    const phoneCall = PhoneCallGenerator.generate(
      supervisedCallInfo.callId,
      callType,
      supervisedCallInfo.from,
    );
    phoneCall.callInfo = new CallInfo({
      isOnHold: false,
      isRecordingPaused: true,
      isMuted: true,
    });
    phoneCall.state = Constants.CALL_STATE.CONNECTED;
    // Set supervisor participant type
    phoneCall.callAttributes = new PhoneCallAttributes({
      voiceCallId: supervisedCallInfo.voiceCallId,
      participantType: Constants.PARTICIPANT_TYPE.SUPERVISOR,
    });
    if (callType == Constants.CALL_TYPE.OUTBOUND) {
      const [voiceObject] = await Promise.all([
        ScvObjectManager.searchVoiceObjectForOutboundCall(
          '',
          supervisedCallInfo.voiceCallId,
        ),
      ]);
      if (voiceObject) {
        if (voiceObject.aircallscv2gp__Aircall_CallUuid__c) {
          supervisedCallInfo.callId =
            voiceObject.aircallscv2gp__Aircall_CallUuid__c;
          phoneCall.callId = supervisedCallInfo.callId;
        }
      }
    }
    const call = {
      id: phoneCall.callId,
      to: phoneCall.phoneNumber,
      from: phoneCall.contact?.phoneNumber ?? '',
      type: CallTypes.SUPERVISOR,
      status: CallStatuses.CONNECTED,
      isMuted: phoneCall.callInfo?.isMuted ?? false,
    };
    this.callRepository.insertCall(call);
    const controller = ControllerFactory.buildSupervisorListenInController();
    if (callType == Constants.CALL_TYPE.INBOUND) {
      await controller.handle({
        call: phoneCall,
        callId: supervisedCallInfo.callId,
        agentNumber: supervisedCallInfo.from,
      });
    } else {
      await controller.handle({
        call: phoneCall,
        callId: supervisedCallInfo.callId,
        agentNumber: supervisedCallInfo.to,
      });
    }

    this.logger.info('Supervisor listen in', {
      action: 'SupervisorListen',
      callId: supervisedCallInfo.callId,
      agentNumber: supervisedCallInfo.from,
    });

    return Promise.resolve(new SuperviseCallResult({ call: phoneCall }));
  }

  override async supervisorDisconnect(
    supervisedCallInfo: SupervisedCallInfo,
  ): Promise<SupervisorHangupResult> {
    console.log('[Aircall Connector] supervisorDisconnect method', {
      supervisedCallInfo,
    });
    const callType =
      supervisedCallInfo.callType == Constants.CALL_TYPE.INBOUND
        ? Constants.CALL_TYPE.INBOUND
        : Constants.CALL_TYPE.OUTBOUND;
    const phoneCall = PhoneCallGenerator.generate(
      supervisedCallInfo.callId,
      callType,
      supervisedCallInfo.from,
    );
    phoneCall.callInfo = new CallInfo({
      isOnHold: false,
      isRecordingPaused: true,
      isMuted: true,
    });
    phoneCall.state = Constants.CALL_STATE.ENDED;
    // Set supervisor participant type
    phoneCall.callAttributes = new PhoneCallAttributes({
      voiceCallId: supervisedCallInfo.voiceCallId,
      participantType: Constants.PARTICIPANT_TYPE.SUPERVISOR,
    });
    const controller = ControllerFactory.buildSupervisorDisconnectController();
    await controller.handle({
      call: phoneCall,
      callId: supervisedCallInfo.callId,
      agentNumber: supervisedCallInfo.from,
    });
    return Promise.resolve(
      new SupervisorHangupResult({
        calls: phoneCall,
      }),
    );
  }

  override async supervisorBargeIn(
    supervisedCallInfo: SupervisedCallInfo,
  ): Promise<SuperviseCallResult> {
    console.log('[Aircall Connector] supervisorBargeIn method', {
      supervisedCallInfo,
    });
    const callType =
      supervisedCallInfo.callType == Constants.CALL_TYPE.INBOUND
        ? Constants.CALL_TYPE.INBOUND
        : Constants.CALL_TYPE.OUTBOUND;
    const phoneCall = PhoneCallGenerator.generate(
      supervisedCallInfo.callId,
      callType,
      supervisedCallInfo.from,
    );
    phoneCall.callInfo = new CallInfo({
      isOnHold: false,
      isRecordingPaused: true,
      isMuted: true,
    });
    phoneCall.state = Constants.CALL_STATE.CONNECTED;
    // Set supervisor participant type
    phoneCall.callAttributes = new PhoneCallAttributes({
      voiceCallId: supervisedCallInfo.voiceCallId,
      participantType: Constants.PARTICIPANT_TYPE.SUPERVISOR,
    });
    if (callType == Constants.CALL_TYPE.OUTBOUND) {
      const [voiceObject] = await Promise.all([
        ScvObjectManager.searchVoiceObjectForOutboundCall(
          '',
          supervisedCallInfo.voiceCallId,
        ),
      ]);
      if (voiceObject) {
        if (voiceObject.aircallscv2gp__Aircall_CallUuid__c) {
          supervisedCallInfo.callId =
            voiceObject.aircallscv2gp__Aircall_CallUuid__c;
          phoneCall.callId = supervisedCallInfo.callId;
        }
      }
    }
    const controller = ControllerFactory.buildMuteToggleController();
    const putOnMute = true;
    await controller.handle({ onMute: putOnMute });

    this.logger.info('supervisorBargeIn', {
      action: 'supervisorBargeIn',
      callId: supervisedCallInfo.callId,
      agentNumber: supervisedCallInfo.from,
    });

    return Promise.resolve(new SuperviseCallResult({ call: phoneCall }));
  }

  override wrapUpCall(call: PhoneCall): void {
    console.log('[Salesforce Connector] wrapUpCall method', { call });
    const acwEndedController =
      ControllerFactory.buildWrapUpTimeEndedController();
    acwEndedController.handle();
  }
  async removeSupervisor() {
    const call = await this.callRepository.getCurrentCall();
    if (call && call.type === 'Supervisor') {
      const phoneCall = PhoneCallGenerator.generate(
        call.id,
        Constants.CALL_TYPE.INBOUND,
        call.from,
      );
      publishEvent({
        eventType: Constants.VOICE_EVENT_TYPE.SUPERVISOR_HANGUP,
        payload: new SupervisorHangupResult({ calls: phoneCall }),
        registerLog: true,
      });
      await this.callRepository.removeCalls();
      console.log('[Aircall Connector] removeSupervisor method');
    }
    return;
  }
}
